import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translate, SUPPORTED_LANGS } from "@/i18n/dict";

const STORAGE_KEY = "matpro_lang_pref";

const LangContext = createContext({
  lang: "fr",
  setLang: () => {},
  t: (key) => key,
});

function detectInitial() {
  if (typeof window === "undefined") return "fr";
  try {
    const forced = window.localStorage.getItem(STORAGE_KEY);
    if (forced && SUPPORTED_LANGS.includes(forced)) return forced;
  } catch {}
  const nav = (window.navigator?.language || "fr").slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(nav) ? nav : "fr";
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);

  const setLang = useCallback((l) => {
    if (!SUPPORTED_LANGS.includes(l)) return;
    try { window.localStorage.setItem(STORAGE_KEY, l); } catch {}
    setLangState(l);
    // Aussi mettre à jour html[lang] pour l'accessibilité + SEO
    try { document.documentElement.setAttribute("lang", l === "fr" ? "fr-BE" : l); } catch {}
  }, []);

  useEffect(() => {
    try { document.documentElement.setAttribute("lang", lang === "fr" ? "fr-BE" : lang); } catch {}
  }, [lang]);

  const t = useCallback((key, fallback) => translate(key, lang, fallback), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}

/**
 * <T k="key" /> — retourne la traduction correspondant à la clé du dictionnaire.
 * Alternative en-ligne : <T fr="Bonjour" en="Hello" nl="Hallo" /> pour les strings one-off.
 */
export function T({ k, fr, en, nl }) {
  const { lang } = useLang();
  if (k) return translate(k, lang);
  if (fr || en || nl) {
    const map = { fr, en, nl };
    return map[lang] || fr || en || nl || "";
  }
  return "";
}
